import { useEffect, useState } from "react";
import { Loader2, RefreshCw, DollarSign } from "lucide-react";
import { adminListRecurringInvoices, adminUpdateRecurringInvoice } from "@/Calls/callInsuranceAuth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface RecurringInvoice {
    invoice_id: number;
    invoice_number: number;
    invoice_dealerid: number;
    invoice_status: string;
    invoice_autocharge: string | null;
    invc_cret_evry: string | null;
    invc_cret_evrywhn: string | null;
    invoice_recurring_stopdate: string | null;
    fee_description1: string | null;
    invoice_total: string;
    invoice_created_at: string;
}

export default function AdminBillingPage() {
    const [invoices, setInvoices] = useState<RecurringInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState<number | null>(null);

    const load = () => {
        setLoading(true);
        adminListRecurringInvoices()
            .then((d) => setInvoices(d.invoices ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const toggleAutocharge = async (inv: RecurringInvoice) => {
        setUpdating(inv.invoice_id);
        const next = inv.invoice_autocharge === "1" ? "0" : "1";
        await adminUpdateRecurringInvoice(inv.invoice_id, { invoice_autocharge: next });
        setUpdating(null);
        load();
    };

    const toggleStatus = async (inv: RecurringInvoice) => {
        setUpdating(inv.invoice_id);
        const next = inv.invoice_status === "active" ? "inactive" : "active";
        await adminUpdateRecurringInvoice(inv.invoice_id, { invoice_status: next });
        setUpdating(null);
        load();
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Recurring Billing</h1>
                    <p className="text-muted-foreground text-sm mt-1">{invoices.length} recurring invoice{invoices.length !== 1 ? "s" : ""}</p>
                </div>
                <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
            </div>

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b">
                            <tr className="text-left">
                                <th className="p-4 font-medium">Invoice #</th>
                                <th className="p-4 font-medium">Account ID</th>
                                <th className="p-4 font-medium">Description</th>
                                <th className="p-4 font-medium">Frequency</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium">Auto-Charge</th>
                                <th className="p-4 font-medium">Stop Date</th>
                                <th className="p-4 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr key={inv.invoice_id} className="border-b last:border-0 hover:bg-muted/30">
                                    <td className="p-4 font-mono text-xs">{inv.invoice_number}</td>
                                    <td className="p-4">{inv.invoice_dealerid}</td>
                                    <td className="p-4">{inv.fee_description1 ?? "Platform Access"}</td>
                                    <td className="p-4 text-muted-foreground">
                                        Every {inv.invc_cret_evry ?? "1"} {inv.invc_cret_evrywhn ?? "MONTH"}
                                    </td>
                                    <td className="p-4">
                                        {inv.invoice_status === "active"
                                            ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                                            : <Badge variant="outline">Inactive</Badge>
                                        }
                                    </td>
                                    <td className="p-4">
                                        {inv.invoice_autocharge === "1"
                                            ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><DollarSign className="h-3 w-3 mr-1" />On</Badge>
                                            : <Badge variant="outline">Off</Badge>
                                        }
                                    </td>
                                    <td className="p-4 text-muted-foreground text-xs">{inv.invoice_recurring_stopdate ?? "—"}</td>
                                    <td className="p-4">
                                        <div className="flex gap-2 flex-wrap">
                                            <Button
                                                size="sm" variant="outline"
                                                disabled={updating === inv.invoice_id}
                                                onClick={() => toggleAutocharge(inv)}
                                            >
                                                {updating === inv.invoice_id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                                                {inv.invoice_autocharge === "1" ? "Disable Auto" : "Enable Auto"}
                                            </Button>
                                            <Button
                                                size="sm" variant="outline"
                                                disabled={updating === inv.invoice_id}
                                                onClick={() => toggleStatus(inv)}
                                            >
                                                {inv.invoice_status === "active" ? "Deactivate" : "Activate"}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
